<template>
    <div class="tw:w-full activity-generating-placeholder">
        <e-message :type="messageType" :closable="false">
            <div class="tw:flex tw:items-center tw:gap-3">
                <e-icon
                    v-if="!isFailed"
                    icon="circle-notch"
                    spin
                    class="tw:text-xl"
                />
                <e-icon
                    v-else
                    icon="exclamation-triangle"
                    class="tw:text-xl"
                />
                <div>
                    <p class="tw:font-medium tw:m-0">{{ title }}</p>
                    <p class="tw:text-sm tw:opacity-80 tw:m-0">{{ description }}</p>
                    <p v-if="generationError" class="tw:text-sm tw:mt-2 tw:m-0">
                        {{ generationError }}
                    </p>
                </div>
            </div>
        </e-message>
    </div>
</template>

<script>
/**
 * Placeholder shown inside an activity player while AI-generated content
 * (slide deck, quiz, flashcard set) is still being prepared by the
 * post-launch Dispatch*GenerationsJob. The dispatch jobs update the
 * underlying *ActivityTemplate, which is then mirrored onto the live
 * *Activity row by *ActivityTemplateObserver — players only need to poll
 * their own show endpoint to pick up the change.
 */
const ACTIVITY_LABELS = {
    'slide-deck': 'slide deck',
    quiz: 'quiz',
    flashcard: 'flashcard set',
    podcast: 'podcast',
    video: 'video',
};

export default {
    name: 'ActivityGeneratingPlaceholder',

    props: {
        activityType: {
            type: String,
            required: true,
        },
        generationStatus: {
            type: String,
            default: null,
        },
        generationError: {
            type: String,
            default: null,
        },
    },

    computed: {
        isFailed() {
            return this.generationStatus === 'failed';
        },
        messageType() {
            return this.isFailed ? 'is-danger' : 'is-info';
        },
        contentLabel() {
            return ACTIVITY_LABELS[this.activityType] || 'activity content';
        },
        title() {
            if (this.isFailed) {
                return `We couldn't generate this ${this.contentLabel}.`;
            }
            return `Your ${this.contentLabel} is being prepared.`;
        },
        description() {
            if (this.isFailed) {
                return 'You can retry generation from the activity template, or contact your instructor.';
            }
            return "EdverumAI is finishing this content in the background. This page will update automatically when it's ready.";
        },
    },
};
</script>

<style scoped>
.activity-generating-placeholder {
    max-width: 720px;
}
</style>
